'use client'

/**
 * Masjid location & address editor (Masjid OS → Settings). Committee / masjid_admin only — a
 * volunteer/representative gets the COMMITTEE_ONLY lock + a "Suggest an edit" link to the public flow.
 * Drag the pin (or "Use my current location"), which reverse-geocodes to prefill city/district/state/
 * pincode (all editable). Save applies immediately for a ≤2 km move within the same city/state, else it
 * goes to the moderation queue. Labels honor the app language (EN/UR/HI; Urdu RTL).
 */
import { useCallback, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useLanguage } from '../contexts/LanguageContext'

const MasjidLocationMap = dynamic(() => import('./MasjidLocationMap'), { ssr: false, loading: () => <div style={{ height: 300, background: '#f1f0ec', borderRadius: 12 }} /> })

type Loc = { latitude: number | null; longitude: number | null; addressLine1: string | null; area: string | null; city: string | null; district: string | null; state: string | null; pincode: string | null }
const INDIA = { lat: 22.5, lng: 79.0 }

export default function MasjidLocationEditor({ masjidId, slug, mode = 'committee', initial }: {
  masjidId?: string
  slug?: string
  /** 'committee' = Masjid OS settings (apply/queue via the dashboard route); 'suggest' = ANY logged-in
   *  user proposes a change (always queued for review via the public contribution endpoint). */
  mode?: 'committee' | 'suggest'
  /** suggest mode only: the masjid's CURRENT location, used to seed the form without the committee GET. */
  initial?: Partial<Loc>
}) {
  const { lang } = useLanguage()
  const rtl = lang === 'ur'
  const suggest = mode === 'suggest'
  const [form, setForm] = useState<Loc>({ latitude: null, longitude: null, addressLine1: '', area: '', city: '', district: '', state: '', pincode: '' })
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [locked, setLocked] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sent, setSent] = useState(false)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'pending' | 'err'; text: string } | null>(null)
  const [warnings, setWarnings] = useState<string[]>([])
  const [confirmWarn, setConfirmWarn] = useState(false)
  const [pending, setPending] = useState<{ city: string; state: string; distanceM: number | null } | null>(null)
  const [geoBusy, setGeoBusy] = useState(false)

  const T = (en: string, ur: string, hi: string) => (lang === 'ur' ? ur : lang === 'hi' ? hi : en)

  useEffect(() => {
    let active = true
    setLoading(true); setLocked(false); setMsg(null)
    // SUGGEST mode: no committee GET (it 403s for a non-committee user) — seed from the current values
    // passed in by the profile page, which already has them.
    if (suggest) {
      const l = initial || {}
      setForm({ latitude: l.latitude ?? null, longitude: l.longitude ?? null, addressLine1: l.addressLine1 || '', area: l.area || '', city: l.city || '', district: l.district || '', state: l.state || '', pincode: l.pincode || '' })
      setLoading(false)
      return () => { active = false }
    }
    const qs = masjidId ? `?masjidId=${encodeURIComponent(masjidId)}` : slug ? `?slug=${encodeURIComponent(slug)}` : ''
    fetch(`/api/dashboard/masjid-location${qs}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 403) { const d = await r.json().catch(() => ({})); if (d.code === 'COMMITTEE_ONLY') { setLocked(true); return null } }
        return r.ok ? r.json() : null
      })
      .then((d) => {
        if (!active || !d) return
        const l = d.location || {}
        setForm({ latitude: l.latitude ?? null, longitude: l.longitude ?? null, addressLine1: l.addressLine1 || '', area: l.area || '', city: l.city || '', district: l.district || '', state: l.state || '', pincode: l.pincode || '' })
        setPending(d.pending ? { city: d.pending.city, state: d.pending.state, distanceM: d.pending.distanceM } : null)
      })
      .finally(() => active && setLoading(false))
    return () => { active = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masjidId, slug, suggest])

  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    try {
      const r = await fetch(`/api/geocode/reverse?lat=${lat}&lon=${lng}`)
      if (!r.ok) return
      const d = await r.json()
      const a = d.address || {}
      setForm((p) => ({
        ...p,
        city: a.city || a.town || a.village || a.municipality || p.city,
        district: a.state_district || a.county || p.district,
        state: a.state || p.state,
        pincode: a.postcode ? String(a.postcode).replace(/\D/g, '').slice(0, 6) : p.pincode,
      }))
    } catch { /* reverse-geocode unavailable → keep typed values */ }
  }, [])

  const setPin = useCallback((lat: number, lng: number) => {
    setForm((p) => ({ ...p, latitude: lat, longitude: lng }))
    void reverseGeocode(lat, lng)
  }, [reverseGeocode])

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) return
    setGeoBusy(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeoBusy(false); setPin(pos.coords.latitude, pos.coords.longitude) },
      () => { setGeoBusy(false); setMsg({ tone: 'err', text: T('Could not get your location.', 'آپ کا مقام حاصل نہیں ہو سکا۔', 'आपका स्थान नहीं मिल सका।') }) },
      { enableHighAccuracy: true, timeout: 10000 },
    )
  }, [setPin])

  const save = useCallback(async () => {
    setSaving(true); setMsg(null)
    // SUGGEST mode: any logged-in user proposes a change → always queued for review. A pin is required
    // (the map coordinate is what the moderator reviews old-vs-new); address fields ride along.
    if (suggest) {
      try {
        if (form.latitude == null || form.longitude == null) {
          setMsg({ tone: 'err', text: T('Please set the location pin on the map first.', 'براہ کرم پہلے نقشے پر مقام کا پن لگائیں۔', 'कृपया पहले मानचित्र पर स्थान पिन सेट करें।') })
          return
        }
        const ref = masjidId || slug || ''
        const r = await fetch(`/api/masjids/${encodeURIComponent(ref)}/suggest-correction`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({
            issueType: 'wrong_address',
            latitude: form.latitude, longitude: form.longitude,
            address: { addressLine1: form.addressLine1, area: form.area, city: form.city, district: form.district, state: form.state, pincode: form.pincode },
            ...(note.trim() ? { note: note.trim().slice(0, 400) } : {}),
          }),
        })
        if (r.status === 401) { setMsg({ tone: 'err', text: T('Please sign in to suggest a change.', 'تبدیلی تجویز کرنے کے لیے سائن اِن کریں۔', 'बदलाव सुझाने के लिए साइन इन करें।') }); return }
        const d = await r.json().catch(() => ({}))
        if (!r.ok) { setMsg({ tone: 'err', text: d.error || T('Could not submit.', 'جمع نہیں ہو سکا۔', 'सबमिट नहीं हो सका।') }); return }
        setSent(true)
        setMsg({ tone: 'pending', text: T('Thank you — your suggestion was sent for review. The page keeps the current location until a moderator approves it.', 'شکریہ — آپ کی تجویز جائزے کے لیے بھیج دی گئی۔ منظوری تک صفحہ موجودہ مقام رکھے گا۔', 'धन्यवाद — आपका सुझाव समीक्षा के लिए भेजा गया। मंज़ूरी तक पेज मौजूदा स्थान रखेगा।') })
      } finally { setSaving(false) }
      return
    }
    try {
      const r = await fetch('/api/dashboard/masjid-location', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ masjidId, slug, ...form }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) {
        if (d.warnings?.length) { setWarnings(d.warnings); setConfirmWarn(true) }
        setMsg({ tone: 'err', text: d.error || T('Could not save.', 'محفوظ نہیں ہو سکا۔', 'सहेजा नहीं जा सका।') })
        return
      }
      setWarnings(d.warnings || [])
      if (d.applied) setMsg({ tone: 'ok', text: T('Location updated.', 'مقام اپ ڈیٹ ہو گیا۔', 'स्थान अपडेट हो गया।') })
      else if (d.pending) { setPending({ city: form.city || '', state: form.state || '', distanceM: d.distanceM }); setMsg({ tone: 'pending', text: T('Sent for review — the public page keeps the current location until approved.', 'جائزے کے لیے بھیج دیا گیا — منظوری تک عوامی صفحہ موجودہ مقام رکھے گا۔', 'समीक्षा के लिए भेजा गया — मंज़ूरी तक सार्वजनिक पेज मौजूदा स्थान रखेगा।') }) }
    } finally { setSaving(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, masjidId, slug, suggest, note])

  if (loading) return <div style={{ padding: 16, color: '#6b7280' }}>…</div>
  if (locked) {
    return (
      <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: 16 }}>
        <div style={{ fontWeight: 800, color: '#9a3412', marginBottom: 4 }}>🔒 {T('Committee only', 'صرف کمیٹی', 'केवल समिति')}</div>
        <div style={{ fontSize: 13, color: '#7c2d12', marginBottom: 10 }}>{T('Editing the masjid location is limited to committee members.', 'مسجد کا مقام صرف کمیٹی کے ارکان میں ترمیم کر سکتے ہیں۔', 'मस्जिद का स्थान केवल समिति सदस्य संपादित कर सकते हैं।')}</div>
        {slug && <Link href={`/masjids/${slug}/report?issue=wrong_address`} style={{ color: '#0E7C61', fontWeight: 700 }}>{T('Suggest an edit →', 'ترمیم تجویز کریں →', 'संपादन सुझाएँ →')}</Link>}
      </div>
    )
  }

  const lbl = (en: string, ur: string, hi: string) => <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, textAlign: rtl ? 'right' : 'left' }}>{T(en, ur, hi)}</span>
  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, direction: rtl ? 'rtl' : 'ltr' }

  return (
    <div style={{ display: 'grid', gap: 14, direction: rtl ? 'rtl' : 'ltr' }}>
      {pending && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, color: '#92400e' }}>
          {T('A location change is awaiting review', 'ایک مقام کی تبدیلی جائزے کے انتظار میں ہے', 'एक स्थान परिवर्तन समीक्षा की प्रतीक्षा में है')}{pending.distanceM != null ? ` (${(pending.distanceM / 1000).toFixed(1)} km)` : ''}.
        </div>
      )}
      <div>
        {lbl('Masjid location (drag the pin)', 'مسجد کا مقام (پن گھسیٹیں)', 'मस्जिद का स्थान (पिन खींचें)')}
        <MasjidLocationMap lat={form.latitude ?? INDIA.lat} lng={form.longitude ?? INDIA.lng} onChange={setPin} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={useMyLocation} disabled={geoBusy} style={{ background: '#0E7C61', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            📍 {geoBusy ? T('Locating…', 'تلاش…', 'खोज रहे…') : T('Use my current location', 'میرا موجودہ مقام استعمال کریں', 'मेरा वर्तमान स्थान उपयोग करें')}
          </button>
          <span style={{ fontSize: 11.5, color: '#6b7280' }}>{T('Used only to set the masjid’s location.', 'صرف مسجد کا مقام مقرر کرنے کے لیے۔', 'केवल मस्जिद का स्थान तय करने के लिए।')}</span>
          {form.latitude != null && <span style={{ fontSize: 11.5, color: '#9ca3af', fontFamily: 'monospace' }}>{Number(form.latitude).toFixed(5)}, {Number(form.longitude).toFixed(5)}</span>}
        </div>
      </div>
      <label>{lbl('Street address', 'گلی کا پتہ', 'गली का पता')}<input style={inp} value={form.addressLine1 || ''} onChange={(e) => setForm((p) => ({ ...p, addressLine1: e.target.value }))} maxLength={300} /></label>
      <label>{lbl('Area / locality', 'علاقہ', 'क्षेत्र')}<input style={inp} value={form.area || ''} onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))} maxLength={120} /></label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label>{lbl('City', 'شہر', 'शहर')}<input style={inp} value={form.city || ''} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} maxLength={120} /></label>
        <label>{lbl('District', 'ضلع', 'ज़िला')}<input style={inp} value={form.district || ''} onChange={(e) => setForm((p) => ({ ...p, district: e.target.value }))} maxLength={120} /></label>
        <label>{lbl('State', 'ریاست', 'राज्य')}<input style={inp} value={form.state || ''} onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))} maxLength={120} /></label>
        <label>{lbl('Pincode', 'پن کوڈ', 'पिनकोड')}<input style={inp} value={form.pincode || ''} onChange={(e) => setForm((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} inputMode="numeric" maxLength={6} /></label>
      </div>
      {suggest && (
        <label>{lbl('Note for reviewers (optional)', 'جائزہ لینے والوں کے لیے نوٹ (اختیاری)', 'समीक्षकों के लिए नोट (वैकल्पिक)')}
          <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={note} onChange={(e) => setNote(e.target.value.slice(0, 400))} maxLength={400} placeholder={T('What changed? e.g. the masjid moved to a new building.', 'کیا بدلا؟ مثلاً مسجد نئی عمارت میں منتقل ہو گئی۔', 'क्या बदला? जैसे मस्जिद नई इमारत में चली गई।')} />
        </label>
      )}
      {warnings.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, color: '#b91c1c' }}>
          {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
          {confirmWarn && <div style={{ marginTop: 6, fontWeight: 700 }}>{T('Please correct the city/state, then save again.', 'براہ کرم شہر/ریاست درست کریں، پھر دوبارہ محفوظ کریں۔', 'कृपया शहर/राज्य ठीक करें, फिर दोबारा सहेजें।')}</div>}
        </div>
      )}
      {msg && <div style={{ fontSize: 13, fontWeight: 700, color: msg.tone === 'ok' ? '#059669' : msg.tone === 'pending' ? '#b45309' : '#b91c1c' }}>{msg.text}</div>}
      {!sent && (
        <div>
          <button type="button" onClick={save} disabled={saving} style={{ background: '#0B132B', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.5 : 1 }}>
            {saving ? (suggest ? T('Sending…', 'بھیجا جا رہا…', 'भेज रहे…') : T('Saving…', 'محفوظ…', 'सहेज रहे…'))
              : suggest ? T('Send for review', 'جائزے کے لیے بھیجیں', 'समीक्षा के लिए भेजें') : T('Save location', 'مقام محفوظ کریں', 'स्थान सहेجें')}
          </button>
        </div>
      )}
    </div>
  )
}
